import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cn } from '#utils'

/**
 * Checkbox component
 * 
 * Accessible checkbox built on Radix UI primitives with ButterUI styling.
 * Supports checked, indeterminate (`checked="indeterminate"` — a dash, e.g. a
 * select-all over a partial selection), and disabled states. Colours are
 * semantic tokens, so it is dark-mode clean; the checked fill stays the Butter
 * brand, not `theme-*`, which changes per white-label club.
 * 
 * @example
 * ```tsx
 * <Checkbox 
 *   id="terms" 
 *   checked={accepted}
 *   onCheckedChange={setAccepted}
 * />
 * <label htmlFor="terms">Accept terms</label>
 * ```
 * 
 * @see https://www.radix-ui.com/primitives/docs/components/checkbox
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/
 */
export interface CheckboxProps extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  // All Radix Checkbox props are inherited
}

export function Checkbox({ className, ref, ...props }: CheckboxProps & { ref?: Ref<ComponentRef<typeof CheckboxPrimitive.Root>> }) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        // Base styles
        'group peer h-4 w-4 shrink-0 rounded border border-edge-strong',
        'ring-offset-surface',
        // Focus styles
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butter-brand-600 focus-visible:ring-offset-2',
        // Disabled state
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Checked state
        'data-[state=checked]:border-butter-brand-600 data-[state=checked]:bg-butter-brand-600 data-[state=checked]:text-white',
        'data-[state=indeterminate]:border-butter-brand-600 data-[state=indeterminate]:bg-butter-brand-600 data-[state=indeterminate]:text-white',
        // User overrides
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn('flex items-center justify-center text-current')}
      >
        {/* Check icon — sized smaller than outer container so it doesn't touch the edges */}
        <svg
          className="h-3 w-3 group-data-[state=indeterminate]:hidden"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg
          className="hidden h-3 w-3 group-data-[state=indeterminate]:block"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <line x1="6" y1="12" x2="18" y2="12" />
        </svg>
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
}
